import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useSettings } from "@/components/layout/SiteLayout";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/work", label: "Work" },
  { to: "/podcast", label: "Podcast Studio" },
  { to: "/ai-solutions", label: "AI Solutions" },
  { to: "/about", label: "About" },
  { to: "/blogs", label: "Blogs" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const settings = useSettings();
  const logoUrl = settings?.logo_url;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 bg-[#030712]/80 backdrop-blur-xl border-b border-white/10"
          : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2 group"
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Rise Rift"
              className="h-5 md:h-6 w-auto object-contain drop-shadow-[0_0_14px_rgba(47,107,255,0.45)]"
            />
          ) : (
            <>
              <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-[#2F6BFF] to-[#4DA8FF] shadow-[0_0_20px_rgba(47,107,255,0.5)]">
                <span className="font-bold text-white text-lg leading-none">R</span>
                <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-[#4DA8FF] rr-pulse" />
              </span>
              <span className="text-white font-semibold tracking-tight text-lg">
                Rise Rift
              </span>
            </>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-full transition-all ${
                  isActive
                    ? "text-white bg-white/10"
                    : "text-slate-300 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/cost-calculator"
            data-testid="nav-calc-btn"
            className="text-sm text-slate-300 hover:text-white transition-colors"
          >
            Estimate cost
          </Link>
          <Link
            to="/contact"
            data-testid="nav-cta-btn"
            className="rr-btn-primary text-sm"
          >
            Book a call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          onClick={() => setOpen((s) => !s)}
          data-testid="nav-menu-toggle"
          className="lg:hidden w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden mt-3 mx-4 rr-glass-strong rounded-2xl p-4"
          >
            <div className="flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`m-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-3 text-sm rounded-xl transition ${
                      isActive
                        ? "text-white bg-white/10"
                        : "text-slate-300 hover:text-white hover:bg-white/5"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="m-nav-cta-btn"
                className="rr-btn-primary mt-2 justify-center"
              >
                Book a call
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
